import React, { useEffect, forwardRef } from 'react';
import { Play, Loader } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import clsx from 'clsx';

interface SubmitButtonProps {
  disabled?: boolean;
  className?: string;
}

// eslint-disable-next-line no-undef
const SubmitButton = forwardRef<HTMLButtonElement, SubmitButtonProps>(({ disabled = false, className = '' }, ref) => {
  const { state, submitJob, stopSubmitPulse } = useApp();
  const { fileQueue, isSubmitting, email, isEmailValid, shouldPulseSubmit } = state;

  // Get only valid files for submission
  const validFiles = fileQueue.filter(f => f.validation.isValid);
  const validCount = validFiles.length;

  const handleSubmit = async () => {
    if (validCount === 0 || isSubmitting || !isEmailValid) return;

    // Stop pulse animation when user clicks submit
    if (shouldPulseSubmit) {
      stopSubmitPulse();
    }

    try {
      await submitJob();
    } catch (error) {
      console.error('❌ Submit failed:', error);
    }
  };

  const isDisabled = disabled || validCount === 0 || isSubmitting || !isEmailValid;
  
  // Auto-stop pulse animation after 15 seconds
  useEffect(() => {
    if (shouldPulseSubmit) {
      const timeout = setTimeout(() => {
        stopSubmitPulse();
      }, 15000); // Stop pulsing after 15 seconds
      
      return () => clearTimeout(timeout);
    }
  }, [shouldPulseSubmit, stopSubmitPulse]);
  
  // Determine disabled reason for tooltip
  const getDisabledReason = (): string => {
    if (validCount === 0) return 'Add valid files to queue before submitting';
    if (!email) return 'Enter your email address first';
    if (!isEmailValid) return 'Enter a valid email address';
    if (isSubmitting) return 'Processing...';
    return '';
  };

  return (
    <button
      ref={ref}
      onClick={handleSubmit}
      disabled={isDisabled}
      type="submit"
      className={clsx(
        'btn-primary flex items-center space-x-2 px-6 py-2.5 rounded-lg font-medium transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
        isDisabled
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
          : 'bg-purple-600 hover:bg-purple-700 text-white shadow-sm hover:shadow-md',
        shouldPulseSubmit && !isDisabled && 'animate-pulse ring-4 ring-purple-300 ring-opacity-50 scale-105',
        className
      )}
      title={getDisabledReason() || (shouldPulseSubmit ? 'Ready to submit! Click to process your images' : `Submit ${validCount} valid file${validCount !== 1 ? 's' : ''} for AI processing and email delivery`)}
      aria-describedby="submit-help"
    >
      {isSubmitting ? (
        <Loader className="w-4 h-4 animate-spin" />
      ) : (
        <Play className="w-4 h-4" />
      )}
      <span>
        {isSubmitting
          ? 'Processing...'
          : shouldPulseSubmit
            ? `Submit ${validCount} images!`
            : `Submit${validCount > 0 ? ` (${validCount})` : ''}`
        }
      </span>
    </button>
  );
});

SubmitButton.displayName = 'SubmitButton';

export default SubmitButton;